import {
  ReactElement,
  useMemo,
  useState
} from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';

import LocationOnIcon from '@mui/icons-material/LocationOn';

import { SxProps } from '@mui/material';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import parse from 'autosuggest-highlight/parse';

import Address, { AddressType, ADDRESS_TYPES } from '../../types/Address';
import GooglePlace from '../../types/GooglePlace';

interface GooglePlaceFieldProps {
  value?: Address;
  label?: string;
  onPlaceSelected: (address: Address) => void;
  defaultCountryCode?: string;
  type?: AddressType;
  sx?: SxProps;
}

export default function GooglePlaceField(props: GooglePlaceFieldProps): ReactElement {
  const { value, label, onPlaceSelected, defaultCountryCode = 'US', type = ADDRESS_TYPES.MAILING, sx } = props;

  const [options, setOptions] = useState<readonly GooglePlace[]>([]);
  const placesLib = useMapsLibrary('places');

  // Convert Address to GooglePlace for display
  const placeDetails = useMemo(() => {
    if (value && value.street1 && value.googlePlaceId) {
      const parts: string[] = [];
      if (value.street1) parts.push(value.street1);
      if (value.city) parts.push(value.city);
      if (value.state) parts.push(value.state);

      return {
        description: parts.join(', '),
        place_id: value.googlePlaceId,
        structured_formatting: {
          main_text: value.name || value.street1,
          main_text_matched_substrings: [],
          secondary_text: parts.join(', ')
        }
      };
    }
    return null;
  }, [value]);

  const fetch = useMemo(
    () =>
      debounce(
        (input: string, callback: (results: GooglePlace[]) => void) => {
          if (!placesLib) { callback([]); return; }
          placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input })
            .then(({ suggestions }) => {
              callback(
                suggestions
                  .filter(s => s.placePrediction)
                  .map(s => ({
                    description: s.placePrediction!.text.text,
                    place_id: s.placePrediction!.placeId,
                    structured_formatting: {
                      main_text: s.placePrediction!.mainText?.text ?? '',
                      main_text_matched_substrings: s.placePrediction!.mainText?.matches?.map(m => ({
                        offset: m.startOffset ?? 0,
                        length: (m.endOffset ?? 0) - (m.startOffset ?? 0),
                      })) ?? [],
                      secondary_text: s.placePrediction!.secondaryText?.text ?? '',
                    }
                  }))
              );
            })
            .catch((err) => { console.error('AutocompleteSuggestion error:', err); callback([]); });
        },
        400,
      ),
    [placesLib],
  );

  // When user selects a place from dropdown, fetch details and convert to Address
  const handlePlaceChange = async (newPlace: GooglePlace | null) => {
    if (!newPlace || !placesLib) return;

    const place = new placesLib.Place({ id: newPlace.place_id });
    await place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'displayName'] });

    if (!place.addressComponents) return;

    const components = place.addressComponents;

    // Extract address components
    let street1 = '';
    let street2 = '';
    let city = '';
    let state = '';
    let postalCode = '';
    let countryCode = defaultCountryCode;
    let countryName = '';

    components.forEach((component) => {
      const types = component.types;

      if (types.includes('street_number')) {
        street1 = (component.longText ?? '') + ' ' + street1;
      }
      if (types.includes('route')) {
        street1 += component.longText ?? '';
      }
      if (types.includes('subpremise')) {
        street2 = component.longText ?? '';
      }
      if (types.includes('locality')) {
        city = component.longText ?? '';
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.shortText ?? '';
      }
      if (types.includes('postal_code')) {
        postalCode = component.longText ?? '';
      }
      if (types.includes('country')) {
        countryCode = component.shortText ?? defaultCountryCode;
        countryName = component.longText ?? '';
      }
    });

    // Determine if the name is a proper place name or just the address
    // If the name matches the street address, it's not a proper name
    const trimmedStreet1 = street1.trim();
    const hasProperName = place.displayName &&
                          place.displayName !== trimmedStreet1 &&
                          !place.displayName.startsWith(trimmedStreet1);

    // Create Address object
    const newAddress: Address = {
      googlePlaceId: newPlace.place_id,
      name: hasProperName ? (place.displayName ?? undefined) : undefined,
      street1: trimmedStreet1,
      street2: street2 || undefined,
      city,
      state: state || undefined,
      postalCode: postalCode || undefined,
      countryCode,
      countryName,
      isDefault: value?.isDefault ?? false,
      type: type,
    };

    onPlaceSelected(newAddress);
  };

  return (
    <Autocomplete
        id="google-place-autocomplete"
        sx={sx ?? { width: '18.75rem' }}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.structured_formatting.main_text
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        clearOnBlur
        includeInputInList
        selectOnFocus
        filterSelectedOptions
        value={placeDetails}
        noOptionsText="No locations"
        onChange={(_event: React.SyntheticEvent, newPlace: GooglePlace | null) => {
          setOptions(newPlace ? [newPlace, ...options] : options);
          if (newPlace) {
            handlePlaceChange(newPlace);
          }
        }}
        onInputChange={(_event, newInputValue) => {
          if (newInputValue && placesLib) {
            fetch(newInputValue, (results: GooglePlace[]) => {
              let newOptions: readonly GooglePlace[] = [];
              if (placeDetails) {
                newOptions = [placeDetails];
              }
              if (results) {
                newOptions = [...newOptions, ...results];
              }
              setOptions(newOptions);
            });
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label={label || 'Add a location'} fullWidth />
        )}
        renderOption={(props, option) => {
          // MUI keys options by their label, and several places can share a name ("Serrano");
          // the Google place id is unique.
          const { key: _labelKey, ...otherProps } = props;
          const key = option.place_id;
          const matches =
            (option.structured_formatting && option.structured_formatting.main_text_matched_substrings) || [];

          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match: { offset: number; length: number }) => [match.offset, match.offset + match.length]),
          );

          return (
            <li key={key} {...otherProps}>
              <Grid container sx={{
                alignItems: "center"
              }}>
                <Grid sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                  {parts.map((part, index) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                    >
                      {part.text}
                    </Box>
                  ))}
                  <Typography variant="bodyMedium" sx={{
                    color: "text.secondary"
                  }}>
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
  );
}
